@let integrationName = serviceIdentifier | integrationNameKey | translate;
@let rateLimitTooltip = isRateLimited ? ("int_invoice_rate_limited_tooltip" | translate: { integrationName }) : "";

<p-dialog
    [(visible)]="showDialog"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    (onHide)="onHide()"
    (onShow)="onShow()"
    [style]="{ width: '650px', 'max-width': '90vw' }"
    [maximizable]="true"
>
    <ng-template pTemplate="header">
        <div class="flex items-center">
            <span>{{ currentLayerName }}</span>
            <i class="pi pi-angle-right mx-2"></i>
            <span>{{ "int_import_label" | translate }}</span>
        </div>
    </ng-template>

    <div class="p-fluid">
        <h4 class="mb-8">{{ "int_import_data_label" | translate: { integrationName } }}</h4>

        <label for="importSource">{{ "int_data_source_dropdown_label" | translate }}</label>

        <div class="field mt-4 flex items-center" [pTooltip]="rateLimitTooltip" [tooltipDisabled]="!isRateLimited" tooltipPosition="top">
            <p-select
                data-testid="integration-import-source"
                [disabled]="importSrcDropDownDisabled"
                id="importSource"
                [options]="items"
                [(ngModel)]="selectedItem"
                optionLabel="name"
                placeholder="Select"
                [showClear]="true"
                (onChange)="onItemSelected($event)"
                (onClear)="resetImportState()"
                optionDisabled="disabled"
                class="flex-1"
            >
                <ng-template pTemplate="selectedItem">
                    @if (selectedItem) {
                        <div class="flex items-center">
                            <span>{{ selectedItem.name }}</span>
                            @if (selectedItem && selectedItem.isConflicting) {
                                @let tooltipText = "int_data_source_auto_import_lock_tooltip" | translate: { layerName: selectedItem!.conflictingLayerName };
                                <i class="pi pi-lock ml-2" pTooltip="{{ tooltipText }}"></i>
                            }
                        </div>
                    }
                </ng-template>
                <ng-template let-option pTemplate="item">
                    <div class="flex items-center justify-between w-full">
                        <span [class]="option.isConflicting ? 'text-400' : ''">{{ option.name }}</span>
                        <span
                            *ngIf="option.isConflicting"
                            class="inline-block"
                            pTooltip="{{ 'int_data_source_auto_import_lock_tooltip' | translate: { layerName: option.conflictingLayerName } }}"
                            tooltipPosition="right"
                            [tooltipDisabled]="false"
                            style="pointer-events: auto"
                        >
                            <i class="pi pi-lock text-surface-400 dark:text-surface-400"></i>
                        </span>
                    </div>
                </ng-template>
            </p-select>
            <i *ngIf="autoImportActiveState" class="pi pi-lock ml-2" style="font-size: 1.2em" pTooltip="{{ 'int_data_source_locked_tooltip' | translate }}"></i>
        </div>

        <!-- Info message when no source is selected -->
        <div *ngIf="!selectedItem && !loading" class="flex mb-4 items-center mt-4 text-surface-500 dark:text-surface-300">
            <i class="pi pi-info-circle mr-2 text-surface-400 dark:text-surface-400"></i>
            <span class="text-sm">{{ "int_data_source_dropdown_info_label" | translate }}</span>
        </div>

        @if (selectedItem && importModes.length > 1) {
            <p-selectButton
                [allowEmpty]="false"
                id="importMode"
                class="w-full mt-6"
                [disabled]="importModeDisabled"
                [pTooltip]="rateLimitTooltip"
                [tooltipDisabled]="!isRateLimited"
                tooltipPosition="top"
                [(ngModel)]="importMode"
                [options]="importModes"
                (onChange)="onImportModeChange($event)"
                optionLabel="name"
            />
        }

        <!-- Automatic Import Tab Content -->
        <div *ngIf="importMode?.value === ImportModeValues.AUTOMATIC" class="mt-6">
            <p-card styleClass="rounded-xl shadow-sm">
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <span
                            [pTooltip]="
                                !currentResourceTypeSupportsAutoSync
                                    ? ('int_auto_sync_not_supported_for_resource_type_tooltip' | translate: { resourceType: selectedItem?.name })
                                    : rateLimitTooltip
                            "
                            [tooltipDisabled]="currentResourceTypeSupportsAutoSync && !isRateLimited"
                            tooltipPosition="top"
                        >
                            <p-toggleswitch
                                [(ngModel)]="enableAutoImport"
                                [disabled]="autoImportSwitchDisabled || loading"
                                (onChange)="onAutoImportChange($event.checked)"
                                inputId="autoImportSwitch"
                            >
                            </p-toggleswitch>
                        </span>
                        <label for="autoImportSwitch" class="ml-4 font-medium">{{ importModes[1].name }}</label>
                        <i *ngIf="loading" class="pi pi-spin pi-spinner text-primary ml-2"></i>
                    </div>
                    <p-tag
                        *ngIf="enableAutoImport && !loading"
                        icon="pi pi-check"
                        severity="success"
                        value="{{ 'int_automatic_import_switch_active_tag_label' | translate }}"
                        class="ml-2"
                    >
                    </p-tag>
                </div>
                <div class="mt-4 text-sm text-surface-600 dark:text-surface-200 leading-normal">
                    {{ "int_automatic_import_switch_description" | translate: { resourceType: selectedItem?.name, integrationName } }}
                </div>
            </p-card>
        </div>

        <!-- Manual Import Tab Content -->
        <div *ngIf="importMode?.value === ImportModeValues.MANUAL" class="mt-6">
            <div *ngIf="loading">
                <p-skeleton width="80%" styleClass="mb-4"></p-skeleton>
                <p-skeleton width="40%" styleClass="mb-2"></p-skeleton>
                <p-skeleton width="80%" styleClass="mb-4"></p-skeleton>
                <p-skeleton width="40%" styleClass="mb-2"></p-skeleton>
                <p-skeleton width="80%" styleClass="mb-4"></p-skeleton>
                <p-skeleton width="40%" styleClass="mb-2"></p-skeleton>
            </div>

            <div class="flex flex-col" *ngIf="!loading && showResources" [pTooltip]="rateLimitTooltip" [tooltipDisabled]="!isRateLimited" tooltipPosition="top">
                <div class="flex items-center justify-between mb-4">
                    <label>
                        {{
                            "int_resource_list_label"
                                | translate: { resourceType: selectedItem?.name, totalCount: totalResourceCount, newCount: newResourceCount, integrationName }
                        }}
                    </label>
                    <button
                        type="submit"
                        pButton
                        severity="secondary"
                        class="p-button-text"
                        icon="pi pi-refresh"
                        [loading]="loading"
                        [disabled]="refreshDisabled"
                        text
                        size="small"
                        (click)="refreshCurrentResources()"
                        pTooltip="{{ 'int_refresh_list_button_tooltip' | translate }}"
                        tooltipPosition="top"
                    ></button>
                </div>
                <p-listbox
                    data-testid="integration-import-resources"
                    [disabled]="resourceListDisabled"
                    [(ngModel)]="selectedResources"
                    [options]="resources"
                    [multiple]="true"
                    [checkbox]="true"
                    optionLabel="name"
                    [filter]="true"
                    class="w-full dynamic-listbox"
                    (onChange)="onResourceSelChange($event)"
                >
                </p-listbox>
            </div>

            <!-- Import Now and Cancel buttons for Manual Import (always show when resources are available -->
            <div class="flex mt-6 justify-end" style="width: 50%; margin-left: auto" *ngIf="showResources">
                <button
                    pButton
                    pRipple
                    type="button"
                    label="{{ 'button_cancel' | translate }}"
                    (click)="onHide()"
                    severity="secondary"
                    class="p-button-text mr-2 whitespace-nowrap"
                ></button>
                <div class="flex items-center" [pTooltip]="rateLimitTooltip" [tooltipDisabled]="!isRateLimited" tooltipPosition="top">
                    <button
                        data-testid="integration-import-submit"
                        id="importBtn"
                        [loading]="loading"
                        pButton
                        pRipple
                        type="button"
                        [label]="'int_import_btn_label' | translate"
                        (click)="handleImport()"
                        [disabled]="importDisabled"
                        class="whitespace-nowrap"
                    ></button>
                    <p-badge *ngIf="selectedResources.length > 0" [value]="selectedResources.length.toString()" severity="info" class="ml-2"> </p-badge>
                </div>
            </div>
        </div>
    </div>

    <ng-template pTemplate="footer">
        @if (importMode?.value === ImportModeValues.AUTOMATIC && enableAutoImport && !loading) {
            <div class="flex justify-end">
                <small class="text-surface-500 dark:text-surface-300">{{ "int_automatic_import_active_footer_note" | translate }}</small>
            </div>
        }
    </ng-template>
</p-dialog>
